import React from 'react'
import styled, { keyframes } from 'styled-components'
import Component from 'decorators/Component'
import withRoute from 'decorators/Route'
import {
  colors,
  colorConstants,
  padding,
  paddingSizes,
  borderRadius,
  borderRadiusSizes,
  dropShadows,
  dropShadowVariations,
  margin,
  marginSizes,
  transition
} from 'theme/default'
import IconCancel from 'components/Icons/IconCancel'
import IconConfirm from 'components/Icons/IconConfirm'
import { inputStyle } from 'components/Input'
import { P, LinkText } from 'components/Typo'
import Link from 'components/Link'
import Flex from 'components/Flex'
import Div from 'components/Div'
import * as C from './constants'
import { borderConfig } from './config'

export const Toolbar = styled(Flex).attrs((props) => ({
  alignContent: 'space-between',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  marginTop: 'small'
}))`
  border-radius: ${borderRadius[borderRadiusSizes.small]};
  background-color: ${colors.background};
  border: 1px solid ${colorConstants.lightGray};
  padding: 3px 6px;
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

const TooltipContainer = styled(Flex).attrs((props) => ({
  alignItems: 'center',
  padding: 'extraSmall'
}))`
  z-index: 10;
  width: 280px;
  background-color: ${colorConstants.lightestGray};
  box-shadow: ${dropShadows[dropShadowVariations.light]};
  border-radius: ${borderRadius[borderRadiusSizes.small]};
  border: 1px solid ${colorConstants.lightGray};
  position: absolute;
  top: 40px;
  right: -40px;
  animation: ${fadeIn} 0.2s ${transition.linear};

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 50px;
    border: 5px solid transparent;
    border-bottom: 5px solid ${colorConstants.lightGray};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 50px;
    border: 4px solid transparent;
    border-bottom: 4px solid ${colorConstants.lightestGray};
  }
`

const TooltipInput = styled.input`
  ${inputStyle}
  width: 195px;
  height: 35px;
  margin-right: ${margin[marginSizes.extraSmall]};
  padding: ${padding[paddingSizes.small]};
  border: 1px solid ${colorConstants.lightGray};

  &::placeholder {
    opacity: 0.5;
  }
`

export const Button = styled.button`
  width: 35px;
  height: 35px;
  background-color: ${props => props.isActive ? colorConstants.lightGray : colorConstants.white};
  border-radius: ${props => props.borderRadius};
  border: 1px solid ${colorConstants.lightGray};

  /* shift -1px left To overlap the border of the button */
  margin: 2.5px 0 2.5px -1px;
  padding: ${padding[paddingSizes.none]};
  display: inline-block;
  text-align: center;
  cursor: pointer;
  ${props => props.withTooltip ? 'position: relative;' : ''};

  &:hover {
    color: ${colorConstants.lightestGray};
  }

  &:active {
    outline: none;
  }
`

export const Tooltip = Component()(({ t, onTooltipCancel, onTooltipConfirm }) => {
  const [href, setHref] = React.useState(null)
  const inputRef = React.useRef()

  const onConfirmClick = React.useCallback((e) => {
    e.preventDefault()
    onTooltipConfirm(href)
  }, [href, onTooltipConfirm])

  const onChange = React.useCallback((e) => {
    e.preventDefault()
    setHref(e.target.value)
  }, [])

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <TooltipContainer>
      <TooltipInput
        placeholder={t('Insert a link')}
        onChange={onChange}
        ref={inputRef} />
      <Button onClick={onTooltipCancel} borderRadius={borderConfig[C.BORDER_LEFT]}>
        <IconCancel fill={colors.heading} width={25} height={25} />
      </Button>
      <Button onClick={onConfirmClick} borderRadius={borderConfig[C.BORDER_RIGHT]}>
        <IconConfirm fill={colors.heading} width={25} height={25} />
      </Button>
    </TooltipContainer>
  )
})

export const Seperator = styled(Div)`
  width: 10px;
  height: 35px;
`

export const RichTextContainer = styled(Div)`
  color: ${props => props.color ? props.color : props.theme.colorBodyFont};
  white-space: pre-wrap;
`

export const BoldMark = ({ children, ...props }) => <b {...props}>{children}</b>

export const ItalicMark = ({ children, ...props }) => <i {...props}>{children}</i>

export const UnderlinedMark = ({ children, ...props }) => <u {...props}>{children}</u>

export const UnHighlightMark = ({ children, ...props }) => <span {...props}>{children}</span>

export const HighlightMark = ({ children, ...props }) => {
  return <span style={{ backgroundColor: C.HIGHLIGHT_COLOR }} {...props}>{children}</span>
}

export const LinkMark = Component()(withRoute(({ route, t, href, ...props }) => {
  return (
    <Link {...props} href={href}
      onClick={(e) => {
        if (!route.previewMode || route.previewMode === 'false') return
        e.preventDefault()
        window.confirm(t('This link cannot be opened in customisation preview. It will be opened in a new window (_target_).\nClick OK to continue', { target: href })) && window.open(href, '_blank')
      }} />
  )
}))

export const LinkTextMark = styled(LinkText).attrs((props) => ({
  color: props.theme.buttonColor
}))``

export const BulletedListBlock = styled.ul`
  ${props => props.normalised ? 'margin: 0' : ''};
`

export const NumberedListBlock = styled.ol`
  ${props => props.normalised ? 'margin: 0' : ''};
`

export const ListItemBlock = styled.li`
  ${props => props.normalised ? 'margin: 0' : ''};
`

// WORKAROUND:
// Currently the data attribute is not working for the current styled components version being used
// Thus, the style attribute is used
export const ParagraphLeftBlock = ({ children, ...props }) => {
  return <P {...props} style={{ textAlign: C.LEFT }}>{children}</P>
}

export const ParagraphCenterBlock = ({ children, ...props }) => {
  return <P {...props} style={{ textAlign: C.CENTER }}>{children}</P>
}

export const ParagraphRightBlock = ({ children, ...props }) => {
  return <P {...props} style={{ textAlign: C.RIGHT }}>{children}</P>
}

export const ParagraphBlock = ({ children, ...props }) => {
  return <P {...props}>{children}</P>
}
